import { ReplotChartNames } from '@revelio/replots';

import { DataType } from '../../../../DataProvider/types';
import {
  AREA_CHART_DATA_TYPE,
  dataDownloadRegistry,
  MIXED_STATS_DATA_TYPE,
  VOLUME_CHART_DATA_TYPE,
} from '../registry';

export const buildSheetData = (
  dataSource?: unknown,
  dataType?: string,
  chartType?: ReplotChartNames | string | null
): { sheetData: Record<string, string | number>[]; skipHeader: boolean } => {
  if (
    chartType === ReplotChartNames.KDE &&
    dataType === DataType.DISTRIBUTION
  ) {
    dataType = AREA_CHART_DATA_TYPE;
  } else if (
    chartType === ReplotChartNames.VolumeChart &&
    dataType === DataType.TIMESERIES
  ) {
    dataType = VOLUME_CHART_DATA_TYPE;
  } else if (
    chartType === 'mixed-stats' &&
    dataType === MIXED_STATS_DATA_TYPE
  ) {
    dataType = MIXED_STATS_DATA_TYPE;
  }

  if (!dataSource || !dataType) return { sheetData: [], skipHeader: false };

  const converter =
    dataDownloadRegistry[dataType as keyof typeof dataDownloadRegistry];
  if (!converter) return { sheetData: [], skipHeader: false };

  const formattedNames = {
    primary: 'Dimension',
    secondary: 'Secondary Dimension',
    metric: 'Value',
  };

  const result = converter(
    dataSource,
    formattedNames.primary,
    formattedNames.secondary,
    formattedNames.metric
  );

  return {
    sheetData: result.sheetData,
    skipHeader: result.skipHeader ?? false,
  };
};

const processHorizontalShareData = (
  sheetData: Record<string, string | number>[],
  options: { sortLabels: boolean }
) => {
  if (sheetData.length < 3) return null;

  const dataRows = sheetData.slice(2);
  if (dataRows.length === 0) return null;

  const firstRow = dataRows[0] ?? {};
  const keys = Object.keys(firstRow);

  const categoryKey = keys[0];
  const seriesKeys = keys.slice(1).filter((key) => key.includes('Share'));

  const categories = dataRows.map((row) => String(row[categoryKey ?? '']));
  const seriesNames = seriesKeys.map((seriesKey) =>
    seriesKey.replace(' Share', '')
  );

  const invertedSeries = categories.map((category, categoryIndex) => {
    const values = seriesNames.map((_, seriesIndex) => {
      const seriesKey = seriesKeys[seriesIndex];
      return Number(dataRows[categoryIndex]?.[seriesKey ?? ''] || 0);
    });

    return {
      name: category,
      labels: seriesNames,
      values,
    };
  });

  if (!options.sortLabels) return invertedSeries;

  const labelAggregates = seriesNames.map((_, idx) =>
    invertedSeries.reduce((acc, s) => acc + (s.values[idx] ?? 0), 0)
  );
  const sortedLabelIndexes = seriesNames
    .map((_, idx) => idx)
    .sort((a, b) => (labelAggregates[b] ?? 0) - (labelAggregates[a] ?? 0));
  const sortedLabels = sortedLabelIndexes.map((i) => seriesNames[i]);
  const sortedSeries = invertedSeries.map((s) => ({
    ...s,
    labels: sortedLabels,
    values: sortedLabelIndexes.map((i) => s.values[i]),
  }));

  return sortedSeries;
};

const processVolumeData = (sheetData: Record<string, string | number>[]) => {
  if (sheetData.length === 0) return null;

  const firstRow = sheetData[0] ?? {};
  const keys = Object.keys(firstRow);

  const dateKey = keys[0];
  const dates = sheetData.map((row) => String(row[dateKey ?? '']));

  const activeSeries = {
    name: 'Active',
    labels: dates,
    values: sheetData.map((row) => Number(row['Active'] || 0)),
  };

  const newSeries = {
    name: 'New',
    labels: dates,
    values: sheetData.map((row) => Number(row['New'] || 0)),
  };

  const removedSeries = {
    name: 'Removed',
    labels: dates,
    values: sheetData.map((row) => Number(row['Removed'] || 0)),
  };

  return {
    lineData: [activeSeries],
    barData: [newSeries, removedSeries],
  };
};

const processDivergingBarSingleData = (
  sheetData: Record<string, string | number>[]
) => {
  if (sheetData.length === 0) return null;

  const firstRow = sheetData[0] ?? {};
  const keys = Object.keys(firstRow);
  const dimensionKey = keys[0];
  if (!dimensionKey) return null;

  const valueKey = keys
    .slice(1)
    .find((k) => k !== 'Secondary Dimension' && k !== 'Share');
  if (!valueKey) return null;

  const sortedPoints = sheetData
    .map((row, idx) => ({
      label: String(row[dimensionKey] ?? ''),
      value: Number(row[valueKey] ?? 0),
      idx,
    }))
    .sort((a, b) => {
      const delta = a.value - b.value;
      return delta !== 0 ? delta : a.idx - b.idx;
    });

  const categories = sortedPoints.map((p) => p.label);
  const rawValues = sortedPoints.map((p) => p.value);

  const positiveSeries = {
    name: 'Positive',
    labels: categories,
    values: rawValues.map((v) => (v > 0 ? v : 0)),
  };

  const negativeSeries = {
    name: 'Negative',
    labels: categories,
    values: rawValues.map((v) => (v < 0 ? v : 0)),
  };

  return [positiveSeries, negativeSeries];
};

export const processMixedStatsData = (
  sheetData: Record<string, string | number>[]
) => {
  if (sheetData.length === 0) return null;

  const firstRow = sheetData[0] ?? {};
  const keys = Object.keys(firstRow);
  const dateKey = keys[0];
  const seriesKeys = Array.from(
    sheetData.reduce<Set<string>>((acc, row) => {
      Object.keys(row).forEach((k) => {
        if (k !== dateKey) acc.add(k);
      });
      return acc;
    }, new Set<string>())
  );

  const charts = seriesKeys.map((seriesName) => {
    const labels: string[] = [];
    const values: number[] = [];
    sheetData.forEach((row) => {
      const raw = row[seriesName];
      const num =
        raw === '' || raw === undefined || raw === null ? NaN : Number(raw);
      if (!Number.isNaN(num)) {
        labels.push(String(row[dateKey ?? '']));
        values.push(num);
      }
    });

    const firstValue = values[0] ?? 0;
    const lastValue = values[values.length - 1] ?? 0;
    const delta = lastValue - firstValue;
    const deltaPercentage =
      firstValue !== 0 ? (delta / Math.abs(firstValue)) * 100 : 0;

    return {
      name: seriesName,
      labels,
      values,
      lastValue,
      deltaPercentage,
    };
  });

  return charts;
};

export const generateChartData = (
  rowsForChart: Record<string, string | number>[],
  chartType?: ReplotChartNames | string | null
) => {
  if (
    rowsForChart.length === 0 ||
    typeof rowsForChart[0] !== 'object' ||
    rowsForChart[0] === null ||
    Object.keys(rowsForChart[0]).length < 2
  ) {
    return null;
  }

  if (chartType === ReplotChartNames.GroupedBarChartHorizontal) {
    return processHorizontalShareData(rowsForChart, { sortLabels: true });
  } else if (chartType === ReplotChartNames.StackedBarChartHorizontal) {
    return processHorizontalShareData(rowsForChart, { sortLabels: false });
  } else if (chartType === ReplotChartNames.DivergingBarChart) {
    return processDivergingBarSingleData(rowsForChart);
  } else if (chartType === ReplotChartNames.VolumeChart) {
    return processVolumeData(rowsForChart);
  } else if (chartType === 'mixed-stats') {
    return processMixedStatsData(rowsForChart);
  } else if (chartType === ReplotChartNames.BoxPlot) {
    const dimensionKey = Object.keys(rowsForChart[0])[0];
    const categories = rowsForChart.map((r: Record<string, unknown>) =>
      String(r[dimensionKey ?? ''])
    );
    const valueKeys = Object.keys(rowsForChart[0])
      .slice(1)
      .filter((key) => key !== 'Secondary Dimension');

    const displayName = (key: string) => {
      const k = key.toLowerCase();
      if (k.includes('q10') || k.includes('p10')) return 'P10';
      if (k.includes('q50') || k.includes('median') || k === 'q50') {
        return 'Median';
      }
      if (k.includes('q90') || k.includes('p90')) return 'P90';
      return key;
    };

    return valueKeys.map((seriesKey) => {
      const values = categories.map((_, categoryIndex) =>
        Number(rowsForChart[categoryIndex]?.[seriesKey ?? ''] || 0)
      );
      return {
        name: displayName(seriesKey),
        labels: categories,
        values,
      };
    });
  } else {
    const dimensionKey = Object.keys(rowsForChart[0])[0];
    const rawLabels = rowsForChart.map((r: Record<string, unknown>) =>
      String(r[dimensionKey ?? ''])
    );

    const labels = rawLabels.map((label) => {
      if (label.match(/^\d{4}-\d{2}-\d{2}$/) || label.match(/^\d{4}-\d{2}$/)) {
        const date = new Date(label);
        const excelDate = Math.round(
          (date.getTime() - new Date(1900, 0, 1).getTime()) /
            (1000 * 60 * 60 * 24)
        );
        return excelDate;
      }
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      return isNaN(Number(label)) ? label : Number(label);
    });

    const valueKeys = Object.keys(rowsForChart[0]).slice(1);

    return valueKeys.map((seriesName) => ({
      name: seriesName,
      labels,
      values: rowsForChart.map((r: Record<string, unknown>) =>
        Number(r[seriesName])
      ),
    }));
  }
};
